/**
 * AS-11685 — display rules for the live stock columns in the Receive Items
 * dialog. Pure (no Vue, no HTTP) and a cell-for-cell copy of atlas
 * `receiving/liveStockCell.ts` (AS-11684): both apps share the merchant-wide
 * po_receive_columns config, so they must show the same thing (parent D8).
 */
import { LIVE_STOCK_COLUMN_IDS, LiveStockColumnId } from './poReceiveColumns';
import { InventoryQuantityName, LiveLevels, parseInventoryItemId, toInventoryItemIds } from '@/services/inventoryLevels';

export type LiveStockStatus = 'idle' | 'loading' | 'loaded' | 'error';

export const NO_LOCATION_HINT = 'Select a delivery location';
export const NOT_LINKED_HINT = 'Not linked to a Shopify inventory item';
export const NOT_STOCKED = 'Not stocked';
export const DASH = '—';
export const LOADING = '…';

export interface LiveStockCellInput {
  locationId: number | null | undefined;
  inventoryItemId: string | number | null | undefined;
  /** What this receive adds to on_hand for the line's item (see projectedReceiveByItem). */
  receiveNow: number;
  status: LiveStockStatus;
  levels: Record<number, LiveLevels>;
}

export interface LiveStockCellView {
  text: string;
  title?: string;
}

/** The quantity names the visible live columns need — and nothing else. */
export function liveStockNames(visibleColumns: readonly string[]): InventoryQuantityName[] {
  const names: InventoryQuantityName[] = [];
  if (visibleColumns.includes('liveAvailable')) names.push('available');
  if (visibleColumns.includes('liveOnHand') || visibleColumns.includes('liveOnHandAfterReceive')) {
    names.push('on_hand');
  }
  return names;
}

/** The visible live columns, always in display order. */
export function visibleLiveStockColumns(visibleColumns: readonly string[]): LiveStockColumnId[] {
  return LIVE_STOCK_COLUMN_IDS.filter((id) => visibleColumns.includes(id));
}

/**
 * Value key for "what a read would ask for right now", or null when no read
 * is possible (no live column, no location, no linked line). Built from
 * values so rebuilding the item array on every keystroke never re-reads.
 */
export function liveStockRequestKey(
  locationId: number | null | undefined,
  inventoryItemIds: ReadonlyArray<unknown>,
  visibleColumns: readonly string[],
): string | null {
  const names = liveStockNames(visibleColumns);
  if (names.length === 0 || locationId == null) return null;
  const ids = toInventoryItemIds(inventoryItemIds).sort((a, b) => a - b);
  if (ids.length === 0) return null;
  return `${locationId}|${ids.join(',')}|${names.join(',')}`;
}

/**
 * What the dialog may show this render: a read whose key differs from the
 * current one (new line linked, column turned on) is still loading.
 */
export function effectiveLiveStockStatus(
  currentKey: string | null,
  lastKey: string | null,
  storedStatus: LiveStockStatus,
): LiveStockStatus {
  if (currentKey == null) return 'idle';
  if (currentKey !== lastKey) return 'loading';
  return storedStatus;
}

export function liveStockCell(column: LiveStockColumnId, input: LiveStockCellInput): LiveStockCellView {
  if (input.locationId == null) return { text: DASH, title: NO_LOCATION_HINT };
  const id = parseInventoryItemId(input.inventoryItemId);
  if (id == null) return { text: DASH, title: NOT_LINKED_HINT };
  if (input.status === 'error') return { text: DASH };

  const levels = input.levels[id];
  if (!levels && input.status !== 'loaded') return { text: LOADING };

  const value = column === 'liveAvailable' ? levels?.available : levels?.onHand;
  if (column === 'liveOnHandAfterReceive') {
    return value == null ? { text: DASH } : { text: String(value + input.receiveNow) };
  }
  // null and "absent from the answer" both mean Shopify holds no level at this
  // location — distinct from a real 0. A negative stays as Shopify has it.
  return value == null ? { text: NOT_STOCKED } : { text: String(value) };
}

/**
 * What a receive will add to Shopify on_hand, per inventory item. Stock is per
 * item, not per PO line, so lines sharing an item add up; with "Update Shopify
 * Inventory" off the receive moves nothing, so every projection is 0.
 */
export function projectedReceiveByItem(
  lines: ReadonlyArray<{ inventoryItemId: unknown; receiveNow: unknown }>,
  updatesShopifyInventory: boolean,
): Map<number, number> {
  const totals = new Map<number, number>();
  if (!updatesShopifyInventory) return totals;
  for (const line of lines) {
    const id = parseInventoryItemId(line.inventoryItemId);
    const qty = Number(line.receiveNow);
    // Same filter as the submit (processReceiveItems sends only qty > 0 lines).
    if (id == null || !Number.isFinite(qty) || qty <= 0) continue;
    totals.set(id, (totals.get(id) ?? 0) + qty);
  }
  return totals;
}

export function projectedReceiveFor(totals: Map<number, number>, inventoryItemId: unknown): number {
  const id = parseInventoryItemId(inventoryItemId);
  return id == null ? 0 : (totals.get(id) ?? 0);
}

/** "Updated just now" / "Updated 3 min ago" for the Refresh stock button. */
export function updatedAgoLabel(fetchedAt: number | null, now: number): string | null {
  if (fetchedAt == null) return null;
  const minutes = Math.floor(Math.max(0, now - fetchedAt) / 60000);
  return minutes < 1 ? 'Updated just now' : `Updated ${minutes} min ago`;
}
